import React, { useState, useEffect } from 'react';
import { fetchReferringAd } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;
const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReferringAd() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchReferringAd({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const totalOrders = rows.reduce((s, r) => s + Number(r.orderCount ?? r.count ?? 0), 0);
  const totalRevenue = rows.reduce((s, r) => s + Number(r.totalRevenue ?? r.totalOrderAmount ?? r.revenue ?? 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Referring Ad Report</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Ad Sources', value: total.toLocaleString() },
            { label: 'Total Orders', value: totalOrders.toLocaleString() },
            { label: 'Total Revenue', value: fmt(totalRevenue) },
          ].map(({ label, value }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Ad Sources {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ad / Referrer</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Avg Order</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const orders = Number(row.orderCount ?? row.count ?? 0);
                    const rev = Number(row.totalRevenue ?? row.totalOrderAmount ?? row.revenue ?? 0);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                        <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600, maxWidth: 220 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.adName ?? row.referringAd ?? row.referrer ?? row.source ?? '—'}
                          </div>
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{orders.toLocaleString()}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>{fmt(rev)}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{orders > 0 ? fmt(rev / orders) : '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                          {totalOrders > 0 ? `${(orders / totalOrders * 100).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
